/**
 * IncidentDetailPage
 * ------------------
 * Public, token-gated incident detail. Linked from every "[Incident]" email
 * we fire — hospital and contractor recipients land here without logging in.
 *
 * They can:
 *   - See the incident details + comment thread
 *   - Post a comment (free-form name)
 *   - Mark resolved (if still ongoing)
 *
 * Route: /incidents/:token
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertOctagon,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  MessageSquare,
  Send,
  User,
  Clock,
} from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const ROLE_STYLES = {
  admin: "bg-violet-100 text-violet-700",
  contractor: "bg-amber-100 text-amber-700",
  hospital: "bg-emerald-100 text-emerald-700",
  guest: "bg-slate-100 text-slate-600",
};

export default function IncidentDetailPage() {
  const { token } = useParams();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Comment + status forms
  const [commentName, setCommentName] = useState("");
  const [commentRole, setCommentRole] = useState("guest");
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: "",
    by_name: "",
    by_role: "guest",
    next_steps: "",
    resolution_notes: "",
  });
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/public/incidents/${token}`);
      setIncident(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Incident not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const postComment = async () => {
    if (!commentText.trim()) {
      toast.error("Write a comment first");
      return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/public/incidents/${token}/comment`, {
        by_name: commentName.trim() || "Guest",
        by_role: commentRole,
        message: commentText.trim(),
      });
      setCommentText("");
      toast.success("Comment posted — the team has been notified");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Comment failed");
    } finally {
      setPosting(false);
    }
  };

  const submitStatus = async () => {
    if (!statusForm.by_name.trim()) {
      toast.error("Your name is required");
      return;
    }
    if (!statusForm.status) {
      toast.error("Pick a status");
      return;
    }
    setSavingStatus(true);
    try {
      await axios.post(`${API}/public/incidents/${token}/status`, {
        status: statusForm.status,
        by_name: statusForm.by_name.trim(),
        by_role: statusForm.by_role,
        next_steps: statusForm.next_steps.trim() || undefined,
        resolution_notes: statusForm.resolution_notes.trim() || undefined,
      });
      toast.success("Status updated — all parties have been notified");
      setStatusOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-xl border border-slate-200 p-8">
          <AlertOctagon className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-1">Link not valid</h1>
          <p className="text-sm text-slate-500">{error || "This incident link has expired or is invalid."}</p>
        </div>
      </div>
    );
  }

  const status = (incident.resolution_status || "ongoing").toLowerCase();
  const isTerminal = status === "completed" || status === "failed";
  const STYLES = {
    pending: { border: "border-amber-200", bg: "bg-amber-50/60", badge: "bg-amber-500", iconColor: "text-amber-600", icon: AlertOctagon, label: "PENDING" },
    ongoing: { border: "border-blue-200", bg: "bg-blue-50/60", badge: "bg-blue-600", iconColor: "text-blue-600", icon: AlertOctagon, label: "ONGOING" },
    completed: { border: "border-emerald-200", bg: "bg-emerald-50/60", badge: "bg-emerald-600", iconColor: "text-emerald-600", icon: CheckCircle2, label: "COMPLETED" },
    failed: { border: "border-rose-300", bg: "bg-rose-50/60", badge: "bg-rose-700", iconColor: "text-rose-700", icon: AlertOctagon, label: "FAILED" },
  };
  const s = STYLES[status] || STYLES.ongoing;
  const StatusIcon = s.icon;
  const wo = incident.work_order || {};

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3" data-testid="incident-public-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header card */}
        <div className={`rounded-xl border p-5 ${s.border} ${s.bg}`}>
          <div className="flex items-start gap-3">
            <StatusIcon className={`w-7 h-7 ${s.iconColor} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`${s.badge} text-white`} data-testid="incident-status-badge">
                  {s.label}
                </Badge>
                <span className="text-xs text-slate-500">
                  Work order <strong className="text-slate-700">{wo.id || incident.work_order_id}</strong>
                </span>
              </div>
              <h1 className="text-xl font-bold text-slate-900" data-testid="incident-title">
                {incident.title}
              </h1>
              <p className="text-xs text-slate-500 mt-1">
                Logged by <strong>{incident.created_by_name || incident.created_by_role}</strong>{" "}
                ({(incident.created_by_role || "").toLowerCase()}) · {fmt(incident.created_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Detail grid */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <Field label="Hospital" value={wo.hospital_name} />
          {wo.facility_name && <Field label="Facility" value={wo.facility_name} />}
          {wo.title && <Field label="Work order" value={wo.title} />}
          <Field label="Occurred at" value={fmt(incident.occurred_at)} />
          {incident.description && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Description
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="incident-description">
                {incident.description}
              </p>
            </div>
          )}
          {incident.notes && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Notes
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{incident.notes}</p>
            </div>
          )}
          {incident.next_steps && (
            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="incident-next-steps-display">
              <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1">
                Next steps
              </div>
              <p className="text-sm text-violet-900 whitespace-pre-wrap">{incident.next_steps}</p>
            </div>
          )}
          {isTerminal && (
            <div className={`rounded-lg border p-3 ${status === "failed" ? "border-rose-200 bg-rose-50/60" : "border-emerald-200 bg-emerald-50/60"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${status === "failed" ? "text-rose-700" : "text-emerald-700"}`}>
                {status === "failed" ? "Failure details" : "Resolution"}
              </div>
              <p className={`text-sm ${status === "failed" ? "text-rose-900" : "text-emerald-900"}`}>
                <strong>{incident.resolved_by_name}</strong> marked this {s.label.toLowerCase()} at{" "}
                <strong>{fmt(incident.resolved_at)}</strong>.
              </p>
              {incident.resolution_notes && (
                <p className={`text-xs mt-1 whitespace-pre-wrap ${status === "failed" ? "text-rose-800" : "text-emerald-800"}`} data-testid="incident-resolution-notes">
                  {incident.resolution_notes}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Status update CTA — any party with the link can transition */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          {!statusOpen ? (
            <Button
              onClick={() => {
                setStatusForm({
                  status: status,
                  by_name: "",
                  by_role: "guest",
                  next_steps: incident.next_steps || "",
                  resolution_notes: "",
                });
                setStatusOpen(true);
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="incident-update-status-btn"
            >
              <ClipboardCheck className="w-4 h-4" />
              Update status (Pending · Ongoing · Completed · Failed)
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: "pending", label: "Pending", sel: "bg-amber-500 text-white border-amber-500", base: "bg-white text-slate-700 border-slate-300 hover:bg-amber-50" },
                  { v: "ongoing", label: "Ongoing", sel: "bg-blue-600 text-white border-blue-600", base: "bg-white text-slate-700 border-slate-300 hover:bg-blue-50" },
                  { v: "completed", label: "Completed", sel: "bg-emerald-600 text-white border-emerald-600", base: "bg-white text-slate-700 border-slate-300 hover:bg-emerald-50" },
                  { v: "failed", label: "Failed", sel: "bg-rose-700 text-white border-rose-700", base: "bg-white text-slate-700 border-slate-300 hover:bg-rose-50" },
                ].map((opt) => {
                  const isSel = statusForm.status === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setStatusForm({ ...statusForm, status: opt.v })}
                      className={`px-3 py-2 rounded-md border text-sm font-medium ${isSel ? opt.sel : opt.base}`}
                      data-testid={`incident-status-pick-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Your name *</Label>
                  <Input
                    value={statusForm.by_name}
                    onChange={(e) => setStatusForm({ ...statusForm, by_name: e.target.value })}
                    placeholder="Who is updating?"
                    data-testid="incident-status-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <select
                    value={statusForm.by_role}
                    onChange={(e) => setStatusForm({ ...statusForm, by_role: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                    data-testid="incident-status-role"
                  >
                    <option value="hospital">Hospital</option>
                    <option value="contractor">Contractor</option>
                    <option value="admin">Sterile Mate</option>
                    <option value="guest">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Next steps</Label>
                <Textarea
                  rows={2}
                  value={statusForm.next_steps}
                  onChange={(e) => setStatusForm({ ...statusForm, next_steps: e.target.value })}
                  placeholder="What needs to happen next? Who's owning it?"
                  data-testid="incident-status-next-steps"
                />
              </div>
              {(statusForm.status === "completed" || statusForm.status === "failed") && (
                <div>
                  <Label className="text-xs">
                    {statusForm.status === "failed" ? "Failure notes" : "Resolution notes"}
                  </Label>
                  <Textarea
                    rows={2}
                    value={statusForm.resolution_notes}
                    onChange={(e) => setStatusForm({ ...statusForm, resolution_notes: e.target.value })}
                    placeholder={statusForm.status === "failed" ? "Why did this fail?" : "How was this resolved?"}
                    data-testid="incident-status-notes"
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setStatusOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitStatus}
                  disabled={savingStatus}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="incident-status-submit"
                >
                  {savingStatus && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                  Update status
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Discussion · {incident.comments?.length || 0}</h3>
          </div>
          {incident.comments?.length > 0 ? (
            <ul className="space-y-3 mb-4">
              {incident.comments.map((c) => (
                <li key={c.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/60" data-testid={`incident-comment-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-sm font-medium text-slate-800">{c.by_name}</span>
                    {c.by_role && (
                      <Badge className={`${ROLE_STYLES[c.by_role] || ROLE_STYLES.guest} text-[10px] uppercase`}>
                        {c.by_role}
                      </Badge>
                    )}
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmt(c.at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 italic mb-4">No comments yet.</p>
          )}

          {/* Post comment */}
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Your name</Label>
                <Input
                  value={commentName}
                  onChange={(e) => setCommentName(e.target.value)}
                  placeholder="Jane Smith"
                  data-testid="incident-comment-name"
                />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <select
                  value={commentRole}
                  onChange={(e) => setCommentRole(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="incident-comment-role"
                >
                  <option value="hospital">Hospital</option>
                  <option value="contractor">Contractor</option>
                  <option value="admin">Sterile Mate</option>
                  <option value="guest">Other</option>
                </select>
              </div>
            </div>
            <Textarea
              rows={3}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment — visible to admin, hospital, and contractor."
              data-testid="incident-comment-message"
            />
            <div className="flex justify-end">
              <Button
                onClick={postComment}
                disabled={posting}
                className="gap-1.5"
                data-testid="incident-comment-submit"
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Post comment
              </Button>
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400">
          This page is shared with admin, hospital, and contractor for work order {wo.id || incident.work_order_id}.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 w-24 shrink-0">{label}</span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}
